import { useCallback, useContext, useEffect, useState } from 'react';
import type {
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
} from 'ag-grid-community';

import aguid from 'lib/aguid';

import useIsCompatibleComparison from '../../../hooks/useIsCompatableComparison';
import useRankingMatrixData from './useRankingMatrixData';
import useColDefs from './useColDefs';
import useChartDrillDowns from '../../../hooks/useChartDrillDowns';
import buildGridOptions from '../../Grid/buildGridOptions';
import useRowHighlighter from '../../../hooks/useRowHighlighter';
import { RowHeightContext } from '../../../contextProviders/SplashScreenProviders/MiscProviders/RowHeightProvider';

const useGridOptions = ({
  colDefs,
  terms,
  onDrillDown,
}: {
  colDefs: ColDef[];
  terms: any[] | undefined;
  onDrillDown: (term: string, ctrlKey?: boolean) => void;
}) => {
  const { rowHeight } = useContext(RowHeightContext);
  const getGridOptions = useCallback((): GridOptions | undefined => {
    if (!terms) {
      return undefined;
    }

    return {
      ...buildGridOptions({
        columnDefs: colDefs,
        rowModelType: 'clientSide' as 'clientSide',
        rowHeight,
      }),
      rowData: terms,
      context: {
        onDrillDown,
      },
      rowSelection: {
        mode: 'multiRow' as const,
        checkboxes: false,
        headerCheckbox: false,
      },
      suppressCellFocus: true,
    };
  }, [colDefs, onDrillDown, rowHeight, terms]);
  const [gridOptions, setGridOptions] = useState<GridOptions | undefined>(() =>
    getGridOptions(),
  );
  useEffect(() => {
    setGridOptions(getGridOptions());
  }, [getGridOptions]);

  return gridOptions;
};

const useRankingMatrixContent = ({
  gadget,
  gridApi,
  setGridApi,
}: {
  gadget: VisualisationDefinitions.RankingMatrix;
  gridApi?: GridApi;
  setGridApi: React.Dispatch<React.SetStateAction<GridApi | undefined>>;
}): {
  isCompatibleComparison: boolean | undefined;
  isLoading: boolean;
  gridOptions: GridOptions | undefined;
  colDefs: ColDef[];
  // The rows, which the AG view takes through gridOptions.rowData
  terms: DataTable.Row[] | undefined;
  totals: any;
  visId: string;
  onDrillDown: (term: string, ctrlKey?: boolean) => void;
  onGridReady: (event: GridReadyEvent) => void;
  disableAutoLayout: boolean;
} => {
  const isCompatibleComparison = useIsCompatibleComparison(gadget);
  const [visId] = useState<string>(() => aguid());

  const { isLoading, terms, totals } = useRankingMatrixData(gadget);
  const { colDefs } = useColDefs(gadget);
  const { onDrillDown } = useChartDrillDowns(
    gadget.groupByField,
    undefined,
    'matrix',
  );
  const gridOptions = useGridOptions({ colDefs, onDrillDown, terms });
  useRowHighlighter({
    gridApi,
    drillField: gadget.groupByField,
    visId,
  });

  const onGridReady = (event: GridReadyEvent) => {
    setGridApi(event.api);
  };

  useEffect(() => {
    if (gridApi) {
      gridApi.setGridOption('columnDefs', colDefs);
    }
  }, [colDefs, gridApi]);

  useEffect(() => {
    if (gridApi && !gridApi.isDestroyed()) {
      gridApi.setGridOption('loading', isLoading);
    }
  }, [gridApi, isLoading]);

  return {
    isCompatibleComparison,
    isLoading,
    gridOptions,
    colDefs,
    terms,
    totals,
    visId,
    onDrillDown,
    onGridReady,
    disableAutoLayout: gadget.gridSizing !== 'fitContent',
  };
};

export default useRankingMatrixContent;
